import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ChapterRow } from '../../../../pages/Exams/generate/types';
import type {
  AnswerLetter, PaperDraft, PaperDraftSaveError, PaperQuestionItem,
} from '../../../api/types';
import { Badge, Button, PillTabs, Spinner, Track } from '../../../components/ui';
import { FaArrowDown, FaArrowUp, FaTrash } from '../../../components/ui/icons';
import { ArrowLeft, ArrowRight, Plus } from 'lucide-react';
import { plural } from '../../../lib/plural';

const ANSWER_LETTERS: AnswerLetter[] = ['A', 'B', 'C', 'D'];
/** Questions listed before "Show all" (design step 2 shows 7). */
const PREVIEW_COUNT = 7;

const FIELD_CLASS =
  'min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3.5 py-2 text-sm ' +
  'text-ink outline-none transition focus-visible:ring-2 focus-visible:ring-accent';

/** What the page's Summary aside shows about the questions under review. */
export interface ReviewSummary {
  total: number; bubbled: number; teacher: number; keyText: string; keyOk: boolean;
  /** 1-based numbers of the teacher-graded questions (the answer-sheet preview lists them). */
  teacherNumbers: number[];
  /** 1-based numbers of the bubbled questions, in sheet-row order (row 1 is Q11 when Q1-10 are written). */
  bubbledNumbers: number[];
  /** Every question valid and the bubbled count within the sheet's limit: steps 3 and 4 may open. */
  canContinue: boolean;
}

function summarize(qs: PaperQuestionItem[], maxBubbled: number): ReviewSummary {
  const teacher = qs.filter((q) => q.graded_by_teacher).length;
  const issues = qs.filter((q) => questionError(q)).length;
  const bubbled = qs.length - teacher;
  return {
    total: qs.length, bubbled, teacher,
    keyText: qs.length === 0 ? 'No questions yet' : issues === 0 ? 'Complete' : `${issues} to fix`,
    keyOk: qs.length > 0 && issues === 0,
    teacherNumbers: qs.flatMap((q, i) => (q.graded_by_teacher ? [i + 1] : [])),
    bubbledNumbers: qs.flatMap((q, i) => (q.graded_by_teacher ? [] : [i + 1])),
    canContinue: qs.length > 0 && issues === 0 && bubbled <= maxBubbled,
  };
}

/** Lets the page header trigger the same PATCH as the in-card "Save changes". */
export interface QuestionsReviewHandle {
  saveChanges: () => Promise<void>;
  /** Store the current questions (the PATCH every route past step 2 must run first); false when it failed. */
  commit: () => Promise<boolean>;
}

export function emptyQuestion(): PaperQuestionItem {
  return {
    text: '', options: ['', '', '', ''], suggested_answer: null,
    suggested_chapter_id: null, confidence: 1, graded_by_teacher: false,
  };
}

function initExpanded(qs: PaperQuestionItem[]): Set<number> {
  const set = new Set<number>();
  qs.forEach((q, i) => { if (q.confidence < 0.6 || !q.text.trim()) set.add(i); });
  return set;
}

/** Missing-key / missing-text validation shown inline, mirrors the backend's
 * save check (api/school_panel/views/paper_drafts.py `_validate_for_save`). */
function questionError(q: PaperQuestionItem): string | null {
  if (!q.text.trim()) return 'Question text is required.';
  if (q.graded_by_teacher) return null;
  const key = q.suggested_answer;
  if (!key) return 'Set the answer key, or mark this "graded by teacher".';
  const idx = ANSWER_LETTERS.indexOf(key);
  const options = q.options ?? ['', '', '', ''];
  const filled = options.filter((o) => o?.trim()).length;
  if (filled < 2) return 'Add at least two options.';
  if (!options[idx]?.trim()) return `Option ${key} needs text — it's the answer key.`;
  return null;
}

export function extractSaveError(e: unknown): PaperDraftSaveError {
  const data = (e as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (data && typeof data === 'object' && 'detail' in (data as Record<string, unknown>)) {
    const d = data as Partial<PaperDraftSaveError>;
    return { detail: String(d.detail ?? 'Could not save the exam.'), questions: Array.isArray(d.questions) ? d.questions : [] };
  }
  return { detail: 'Could not save the exam. Try again.', questions: [] };
}

interface Props {
  draft: PaperDraft;
  chapters: ChapterRow[];
  onManualEntry: () => void;
  /** Reports question counts and key status for the page's Summary aside. */
  onSummary?: (s: ReviewSummary) => void;
  manualPending: boolean;
  patchQuestions: (questions: PaperQuestionItem[]) => Promise<PaperDraft>;
  patchPending: boolean;
  /** The failed save's reply (the page saves at the last step): flags the offending questions. */
  saveError: PaperDraftSaveError | null;
  /** Called once the edited questions are stored; the page opens the next step. */
  onContinue: () => void;
  onBack: () => void;
}

/** Step 2 (Questions & key) of the new-paper-exam wizard, driven by the draft's
 * server status: extracting shows progress, failed offers the manual fallback,
 * ready shows the editable question rows (spec §7, plan B Task 4). */
const QuestionsReview = forwardRef<QuestionsReviewHandle, Props>(function QuestionsReview({
  draft, chapters, onManualEntry, onSummary, manualPending,
  patchQuestions, patchPending, saveError, onContinue, onBack,
}, ref) {
  const [questions, setQuestions] = useState<PaperQuestionItem[]>(draft.questions);
  const [expanded, setExpanded] = useState<Set<number>>(() => initExpanded(draft.questions));
  const [patchErr, setPatchErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const seededStatus = useRef(draft.status);

  // Reseed local edits only when the server-side status actually changes
  // (extraction finishing, or "type manually" flipping failed -> ready) — never
  // on a mid-extraction poll, so in-progress edits are never clobbered.
  useEffect(() => {
    if (seededStatus.current !== draft.status) {
      seededStatus.current = draft.status;
      setQuestions(draft.questions);
      setExpanded(initExpanded(draft.questions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.status]);

  useImperativeHandle(ref, () => ({ saveChanges: handleSaveChanges, commit: handleCommit }));

  const summaryKey = JSON.stringify(summarize(questions, draft.max_bubbled));
  useEffect(() => { onSummary?.(JSON.parse(summaryKey) as ReviewSummary); }, [summaryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (draft.status === 'extracting') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-[18px] border border-line bg-surface-2 px-6 py-10 text-center">
        <Spinner size="md" />
        <p className="text-sm font-semibold text-ink">Reading the paper…</p>
        <p className="text-xs text-muted">This usually takes a minute.</p>
        {draft.files.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs text-muted">
            {draft.files.map((f) => <li key={f.path}>{f.name}</li>)}
          </ul>
        )}
      </div>
    );
  }

  if (draft.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-[18px] border border-line bg-surface-2 px-6 py-10 text-center">
        <p className="text-sm font-semibold text-danger">Extraction failed</p>
        <p className="max-w-md text-sm text-muted">{draft.error || 'The reader could not read the questions from these files.'}</p>
        <Button variant="ghost" loading={manualPending} onClick={onManualEntry}>
          Type questions manually
        </Button>
      </div>
    );
  }

  const chapterName = (id: number | null) => chapters.find((c) => c.id === id)?.name ?? null;

  function update(i: number, patch: Partial<PaperQuestionItem>) {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }
  function updateOption(i: number, optIdx: number, value: string) {
    setQuestions((qs) => qs.map((q, idx) => {
      if (idx !== i) return q;
      const options = [...(q.options ?? ['', '', '', ''])];
      options[optIdx] = value;
      return { ...q, options };
    }));
  }
  function toggleExpanded(i: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }
  function addQuestion() {
    setShowAll(true);
    setQuestions((qs) => {
      const next = [...qs, emptyQuestion()];
      setExpanded((prev) => new Set(prev).add(next.length - 1));
      return next;
    });
  }
  function removeQuestion(i: number) {
    setQuestions((qs) => qs.filter((_, idx) => idx !== i));
    setExpanded((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => { if (idx < i) next.add(idx); else if (idx > i) next.add(idx - 1); });
      return next;
    });
  }
  function moveQuestion(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    setQuestions((qs) => {
      const next = [...qs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setExpanded((prev) => {
      const next = new Set(prev);
      const hasI = prev.has(i);
      const hasJ = prev.has(j);
      if (hasI) next.add(j); else next.delete(j);
      if (hasJ) next.add(i); else next.delete(i);
      return next;
    });
  }

  async function handleSaveChanges() {
    setPatchErr(null);
    try {
      await patchQuestions(questions);
    } catch {
      setPatchErr('Could not save your changes. Try again.');
    }
  }

  async function handleCommit(): Promise<boolean> {
    setPatchErr(null);
    try {
      await patchQuestions(questions);
      return true;
    } catch {
      setPatchErr('Could not save your changes. Try again.');
      return false;
    }
  }

  /** Store the edited questions (the same PATCH the old "Save exam" sent first), then open the next step. */
  async function handleContinue() {
    if (await handleCommit()) onContinue();
  }

  const bubbledCount = questions.filter((q) => !q.graded_by_teacher).length;
  const errors = questions.map(questionError);
  const overBudget = bubbledCount > draft.max_bubbled;
  const canContinue = questions.length > 0 && errors.every((e) => !e) && !overBudget;
  const flaggedNumbers = new Set(saveError?.questions ?? []);
  // A failed save flags questions by number; make sure none stay hidden.
  const allVisible = showAll || questions.length <= PREVIEW_COUNT || flaggedNumbers.size > 0;
  const pageCounts = draft.files.map((f) => f.pages);
  const pagesKnown = draft.files.length > 0 && pageCounts.every((n): n is number => typeof n === 'number');
  const sourceText = pagesKnown
    ? plural(pageCounts.reduce((n, c) => n + (c as number), 0), 'page')
    : plural(draft.files.length, 'file');
  const bubbledPct = draft.max_bubbled > 0 ? (bubbledCount / draft.max_bubbled) * 100 : 0;
  const missingKeys = questions.filter((q) => q.text.trim() && !q.graded_by_teacher && !q.suggested_answer).length;
  const issueCount = errors.filter(Boolean).length;
  const flagText = issueCount === 0
    ? 'Every bubbled question has an answer key.'
    : `${missingKeys > 0 && missingKeys === issueCount
      ? `${plural(missingKeys, 'question')} ${missingKeys === 1 ? 'needs' : 'need'} an answer key.`
      : `${plural(issueCount, 'question')} ${issueCount === 1 ? 'needs' : 'need'} fixing.`} Everything else looks right.`;

  return (
    <div>
      <div className="min-w-0">
        <div className="mb-4 flex flex-wrap items-center gap-3.5 rounded-[14px] bg-surface-2 p-3.5">
          <span className="min-w-[200px] flex-[1_1_260px]">
            <span className="block font-semibold text-ink">Read {plural(questions.length, 'question')} from {sourceText}</span>
            <span className="text-[13px] text-muted">{flagText}</span>
          </span>
          <span className="min-w-[160px] flex-[0_1_220px]">
            <span className="mb-1.5 flex justify-between text-xs">
              <span className="text-muted">Bubble rows used</span>
              <span className="font-mono font-medium text-ink">{bubbledCount} / {draft.max_bubbled}</span>
            </span>
            <Track value={bubbledPct} tone={overBudget ? 'danger' : 'slate'} label="Bubble rows used" />
          </span>
        </div>

        {patchErr && <p className="mb-3 text-sm text-danger">{patchErr}</p>}

        {/* Edits wait while the questions are being stored, so nothing typed in that window is lost. */}
        <fieldset disabled={patchPending} className="m-0 min-w-0 border-0 p-0">
        <div className="flex flex-col gap-2">
          {questions.map((q, i) => {
            if (!allVisible && i >= PREVIEW_COUNT) return null;
            const isExpanded = expanded.has(i);
            const err = errors[i];
            const flagged = flaggedNumbers.has(i + 1);
            const chapter = chapterName(q.suggested_chapter_id);
            const missingKey = !q.graded_by_teacher && !q.suggested_answer && !!q.text.trim();
            return (
              <div
                key={i}
                className={`rounded-[14px] border px-3.5 py-3 ${
                  flagged ? 'border-danger bg-danger-bg ring-1 ring-danger/40' : err ? 'border-accent-line bg-accent-bg/60' : 'border-line bg-surface'
                }`}>
                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
                  <span className="min-w-[30px] font-mono text-xs font-semibold text-muted">#{i + 1}</span>
                  <span className="min-w-0 flex-[1_1_280px]">
                    {!isExpanded && (
                      <>
                        <span className="block truncate font-medium text-ink">{q.text.trim() || '(no text yet)'}</span>
                        <span className="block text-xs text-muted">
                          {chapter ? `${chapter} · ` : ''}
                          <span className={missingKey ? 'text-accent-ink' : ''}>
                            {q.graded_by_teacher ? 'written answer, graded by teacher' : q.suggested_answer ? `key ${q.suggested_answer}` : 'no key found, pick one'}
                          </span>
                        </span>
                      </>
                    )}
                  </span>
                  {!isExpanded && !q.graded_by_teacher && (
                    <span role="group" aria-label={`Answer key for question ${i + 1}`} className="flex gap-1.5">
                      {ANSWER_LETTERS.map((letter) => (
                        <button
                          key={letter} type="button"
                          aria-pressed={q.suggested_answer === letter}
                          aria-label={`Key ${letter} for question ${i + 1}`}
                          onClick={() => update(i, { suggested_answer: letter })}
                          className={`grid h-7 w-7 place-items-center rounded-full border-[1.5px] p-0 font-mono text-[11px] font-semibold transition-colors hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                            q.suggested_answer === letter
                              ? 'border-navy bg-navy text-white'
                              : 'border-control bg-surface text-muted-2'}`}>
                          {letter}
                        </button>
                      ))}
                    </span>
                  )}
                  {!isExpanded && q.graded_by_teacher && <Badge tone="info">Graded by teacher</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => toggleExpanded(i)}>
                    {isExpanded ? 'Collapse' : 'Edit'}
                  </Button>
                </div>

                {isExpanded && (
                  <>
                    <textarea
                      className={`mt-3 ${FIELD_CLASS}`}
                      rows={2}
                      aria-label={`Question ${i + 1} text`}
                      value={q.text}
                      onChange={(e) => update(i, { text: e.target.value })}
                    />

                    <label className="mt-3 flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={q.graded_by_teacher}
                        onChange={(e) => update(i, { graded_by_teacher: e.target.checked })}
                      />
                      Graded by teacher
                    </label>

                    {!q.graded_by_teacher && (
                      <>
                        <ul className="mt-3 space-y-2">
                          {ANSWER_LETTERS.map((letter, optIdx) => (
                            <li key={letter} className="flex items-center gap-2">
                              <span className="w-5 font-mono text-xs font-semibold text-muted">{letter}.</span>
                              <input
                                className={FIELD_CLASS}
                                aria-label={`Question ${i + 1} option ${letter}`}
                                value={q.options?.[optIdx] ?? ''}
                                onChange={(e) => updateOption(i, optIdx, e.target.value)}
                              />
                            </li>
                          ))}
                        </ul>

                        <div
                          role="radiogroup"
                          aria-label={`Answer key for question ${i + 1}`}
                          className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                          <span className="text-muted">Answer key:</span>
                          {ANSWER_LETTERS.map((letter) => (
                            <label key={letter} className="relative inline-flex">
                              <input
                                type="radio"
                                className="peer sr-only"
                                name={`paper-question-${i}-key`}
                                checked={q.suggested_answer === letter}
                                onChange={() => update(i, { suggested_answer: letter })}
                              />
                              <span className="grid min-h-[40px] min-w-[40px] cursor-pointer place-items-center rounded-full border-[1.5px] border-control bg-surface font-mono text-xs font-semibold text-muted-2 peer-checked:border-navy peer-checked:bg-navy peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-accent">
                                {letter}
                              </span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}

                    <div className="mt-3">
                      <p className="mb-1.5 text-xs font-semibold text-muted">Chapter</p>
                      <PillTabs<string>
                        label={`Chapter for question ${i + 1}`} scroll dense
                        value={q.suggested_chapter_id == null ? 'none' : String(q.suggested_chapter_id)}
                        onChange={(k) => update(i, { suggested_chapter_id: k === 'none' ? null : Number(k) })}
                        options={[{ key: 'none', label: 'No chapter' }, ...chapters.map((c) => ({ key: String(c.id), label: c.name }))]} />
                    </div>

                    {err && <p className="mt-3 text-xs text-danger">{err}</p>}
                    {flagged && saveError && <p className="mt-1 text-xs text-danger">{saveError.detail}</p>}

                    <div className="mt-3 flex items-center gap-2">
                      <Button variant="ghost" size="sm" icon={<FaArrowUp />} disabled={i === 0}
                        aria-label={`Move question ${i + 1} up`} onClick={() => moveQuestion(i, -1)}>{''}</Button>
                      <Button variant="ghost" size="sm" icon={<FaArrowDown />} disabled={i === questions.length - 1}
                        aria-label={`Move question ${i + 1} down`} onClick={() => moveQuestion(i, 1)}>{''}</Button>
                      <Button variant="ghost" size="sm" icon={<FaTrash />} onClick={() => removeQuestion(i)}>
                        Remove
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {questions.length === 0 && (
          <p className="mt-3 text-sm text-muted">No questions yet. Add one to get started.</p>
        )}

        <div className="mt-3.5 flex flex-wrap gap-2.5">
          <Button variant="ghost" size="sm" icon={<Plus size={18} strokeWidth={1.8} aria-hidden />} onClick={addQuestion}>Add question</Button>
          {questions.length > PREVIEW_COUNT && flaggedNumbers.size === 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer questions' : `Show all ${questions.length} questions`}
            </Button>
          )}
          <Button variant="ghost" size="sm" loading={patchPending} onClick={handleSaveChanges}>Save changes</Button>
        </div>
        </fieldset>

        {overBudget && (
          <p className="mt-3 text-xs text-danger">
            {bubbledCount} bubbled questions; the answer sheet holds at most {draft.max_bubbled}. Mark the rest "graded by teacher".
          </p>
        )}
        {saveError && <p className="mt-3 text-sm text-danger">{saveError.detail}</p>}

        <div className="mt-[22px] flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-[18px]">
          <Button variant="ghost" icon={<ArrowLeft size={18} strokeWidth={1.8} aria-hidden />} onClick={onBack}>Back</Button>
          <Button onClick={handleContinue} disabled={!canContinue} loading={patchPending} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} className="flex-row-reverse">
            Continue to students
          </Button>
        </div>
      </div>
    </div>
  );
});

export default QuestionsReview;
